import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@deepguide-ai/ui/dialog';
import { Button } from '@deepguide-ai/ui/button';
import { Input } from '@deepguide-ai/ui/input';
import { Label } from '@deepguide-ai/ui/label';
import { Checkbox } from '@deepguide-ai/ui/checkbox';
import { Alert, AlertDescription } from '@deepguide-ai/ui/alert';
import { HelpCircle } from 'lucide-react';

export interface ZendeskConnectOptions {
  subdomain: string;
  features: ('tickets' | 'articles' | 'categories' | 'sections')[];
}

interface ZendeskSubdomainModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (options: ZendeskConnectOptions) => void;
  allowPublishing?: boolean;
}

export const ZendeskSubdomainModal: React.FC<ZendeskSubdomainModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
  allowPublishing = false,
}) => {
  const [subdomain, setSubdomain] = useState('');
  const [error, setError] = useState('');
  const [publishHelpCenter, setPublishHelpCenter] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = () => {
    if (submitting) return;
    // Validate subdomain
    const trimmedSubdomain = subdomain.trim().toLowerCase();
    
    if (!trimmedSubdomain) {
      setError('Please enter your Zendesk subdomain');
      return;
    }

    // Remove .zendesk.com if user included it
    const cleanSubdomain = trimmedSubdomain.replace(/\.zendesk\.com$/i, '');
    
    // Validate subdomain format (alphanumeric and hyphens only)
    if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(cleanSubdomain)) {
      setError('Enter a subdomain of up to 63 letters, numbers, or hyphens, starting and ending with a letter or number.');
      return;
    }

    setError('');
    setSubmitting(true);
    onSubmit({
      subdomain: cleanSubdomain,
      features: [
        'tickets',
        ...(allowPublishing && publishHelpCenter ? ['articles', 'categories', 'sections'] as const : []),
      ],
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Connect to Zendesk</DialogTitle>
          <DialogDescription>
            Authorize read access to support tickets, staff access, and available account security settings.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 px-6 py-4">
          <div className="space-y-2">
            <Label htmlFor="subdomain">Zendesk Subdomain</Label>
            <div className="flex items-center space-x-2">
              <div className="flex-1">
                <Input
                  id="subdomain"
                  placeholder="your-company"
                  value={subdomain}
                  disabled={submitting}
                  autoComplete="off"
                  aria-invalid={!!error}
                  aria-describedby={error ? 'zendesk-subdomain-error' : 'zendesk-subdomain-help'}
                  onChange={(e) => {
                    setSubdomain(e.target.value);
                    setError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSubmit();
                    }
                  }}
                />
                <p id="zendesk-subdomain-help" className="text-sm text-muted-foreground mt-1">
                  For acme.zendesk.com, enter acme.
                </p>
              </div>
            </div>
            {error && (
              <p id="zendesk-subdomain-error" role="alert" className="text-sm text-destructive">{error}</p>
            )}
          </div>

          <Alert role="note">
            <HelpCircle className="h-4 w-4" />
            <AlertDescription>
              Zendesk administrators can grant security-settings access. Available data depends on your Zendesk role and plan.
              Reported local 2FA and account policy settings do not prove effective MFA protection. Optional ticket webhooks can be bound after connecting using the same read access.
            </AlertDescription>
          </Alert>

          {allowPublishing && (
            <div className="flex items-start gap-2">
              <Checkbox id="zendesk-publishing" checked={publishHelpCenter} disabled={submitting}
                onCheckedChange={checked => setPublishHelpCenter(checked === true)} />
              <div className="space-y-1">
                <Label htmlFor="zendesk-publishing">Allow Help Center publishing</Label>
                <p className="text-sm text-muted-foreground">Also allow creating and updating Help Center articles, categories, and sections.</p>
              </div>
            </div>
          )}

        </div>

        <DialogFooter>
          <Button variant="outline" disabled={submitting} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={submitting} onClick={handleSubmit}>
            {submitting ? 'Connecting…' : 'Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
